import { useEffect, useMemo, useState } from 'react'
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useAuth } from '../lib/AuthContext'
import { useSpecialJerseyList } from '../lib/useSpecialJerseys'
import { loadPerson, savePerson, resolvePerson } from '../lib/personAvatar'
import { setCachedAvatar } from '../lib/useAvatars'
import { fonts, radius } from '../lib/theme'
import { useThemeColors } from '../lib/useThemeColors'
import PersonAvatar from './PersonAvatar'
import {
  PERSON_STYLES, PERSON_HEADWEAR, PERSON_JERSEYS_SELECTABLE, PERSON_PALETTES, PERSON_COLOR_PALETTE, PERSON_DEFAULT, PERSON_BEARDS, BEARD_LIGHT, PERSON_FACES, PERSON_BROWS, onColor,
} from './personAvatarConfig'

// Editor dell'avatar persona (Profilo, solo admin: beta). "Salva" scrive in
// dim_user.avatar_settings.person (vedi lib/personAvatar.js). Non cambia l'avatar mostrato altrove.
//
// Struttura (progressive disclosure): anteprima + 4 schede (Viso, Capelli, Accessori, Maglia).
// In ogni scheda: le scelte, poi i "colori" come chip con il pallino del colore attuale e UNA sola palette
// condivisa sotto (si cambia il colore del chip selezionato). Niente righe a tendina.

const TABS = [
  { key: 'face',   icon: 'emoticon-happy-outline', k: 'tab_face' },
  { key: 'hair',   icon: 'content-cut',            k: 'tab_hair' },
  { key: 'gear',   icon: 'glasses',                k: 'tab_gear' },
  { key: 'jersey', icon: 'tshirt-crew-outline',    k: 'tab_jersey' },
]

function Segment({ options, selected, onSelect, tc, styles }) {
  const { t } = useTranslation()
  return (
    <View style={styles.segmentRow}>
      {options.map(o => {
        const on = selected === o.key
        return (
          <TouchableOpacity
            key={String(o.key)}
            onPress={() => onSelect(o.key)}
            accessibilityRole="button"
            accessibilityState={{ selected: on }}
            style={[styles.segmentBtn, { backgroundColor: on ? tc.rosa : tc.bgCard2, borderColor: on ? tc.rosa : tc.border }]}
          >
            <Text style={[styles.segmentText, { color: on ? tc.brandText : tc.textMuted }]} numberOfLines={1}>{t(`person.${o.k}`)}</Text>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

// Chip dei colori della scheda: etichetta + pallino del colore attuale. Quello attivo e' quello che la palette modifica.
function SlotChips({ slots, active, onSelect, values, tc, styles }) {
  return (
    <View style={styles.slotRow}>
      {slots.map(sl => {
        const on = sl.key === active
        return (
          <TouchableOpacity
            key={sl.key}
            onPress={() => onSelect(sl.key)}
            accessibilityRole="button"
            accessibilityLabel={sl.label}
            accessibilityState={{ selected: on }}
            style={[styles.slotChip, { borderColor: on ? tc.rosa : tc.border, backgroundColor: on ? tc.rosaSubtle : tc.bgCard2 }]}
          >
            <View style={[styles.slotDot, { backgroundColor: values[sl.key], borderColor: tc.borderLight ?? tc.border }]} />
            <Text style={[styles.slotText, { color: on ? tc.text : tc.textMuted }]} numberOfLines={1}>{sl.label}</Text>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

// Palette condivisa: stato selezionato = bordo + spunta; area toccabile 36 + hitSlop 4 = 44
function Palette({ colors, value, onSelect, label, tc, styles }) {
  return (
    <View style={styles.swatchRow}>
      {colors.map((c, i) => {
        const selected = value === c
        return (
          <TouchableOpacity
            key={c + i}
            onPress={() => onSelect(c)}
            hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
            accessibilityRole="button"
            accessibilityLabel={`${label} ${i + 1}`}
            accessibilityState={{ selected }}
            style={[styles.swatch, { backgroundColor: c, borderColor: selected ? tc.rosa : tc.borderLight ?? tc.border, borderWidth: selected ? 3 : 1 }]}
          >
            {selected && <MaterialCommunityIcons name="check" size={18} color={onColor(c)} />}
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

export default function PersonAvatarLab() {
  const { t, i18n } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { user, setPersonAvatar, leagueName } = useAuth()
  // Maglia speciale che indosso nella lega attiva (es. campione del mondo): l'anteprima puo' mostrarla
  const specials = useSpecialJerseyList(user?.id)      // in ordine di priorita'
  const [view, setView] = useState('mine')            // 'mine' oppure il codice di una maglia speciale
  const special = specials.find(x => x.code === view) ?? null
  const [s, setS] = useState(PERSON_DEFAULT)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('face')
  const [slot, setSlot] = useState({})        // chip colore attivo, per scheda
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    if (!user?.id) { setLoading(false); return }
    loadPerson(user.id).then(({ person }) => {
      if (person) setS(person)
      setLoading(false)
    })
  }, [user?.id])

  const update = (patch) => { setS(prev => ({ ...prev, ...patch })); setMsg(null) }

  const handleSave = async () => {
    if (!user?.id) return
    setSaving(true); setMsg(null)
    const { error } = await savePerson(user.id, s)
    setSaving(false)
    if (!error) { setPersonAvatar(s); setCachedAvatar(user.id, { person: s }) }
    setMsg(error ? { ok: false, text: t('person.save_error') } : { ok: true, text: t('person.saved') })
  }

  // Torna all'impostazione di default: toglie `person` dal DB
  const handleReset = async () => {
    if (!user?.id) return
    setSaving(true); setMsg(null)
    const { error } = await savePerson(user.id, null)
    setSaving(false)
    if (error) { setMsg({ ok: false, text: t('person.save_error') }); return }
    setPersonAvatar(null)
    setS(resolvePerson(null, user.id))
    setMsg({ ok: true, text: t('person.reset_done') })
  }

  // Colori della scheda attiva (i chip). Capelli e sopracciglia hanno lo stesso colore; col rasato restano le sole sopracciglia.
  const shaved = s.gender === 'man' && s.style === 'h4'
  const slotsByTab = useMemo(() => {
    const classic = s.jersey === 3
    const gear = []
    if (s.headwear !== 'none') {
      gear.push({ key: 'wear', label: t('person.row_wear') })
      gear.push({ key: 'detail', label: t(s.headwear === 'cap' ? 'person.row_visor' : 'person.row_vents') })
    }
    const glassSlots = s.glasses ? [{ key: 'frame', label: t('person.row_frame') }, { key: 'lens', label: t('person.row_lens') }] : []
    return {
      glass: glassSlots,
      face: [{ key: 'skin', label: t('person.row_skin') }, { key: 'iris', label: t('person.row_iris') }],
      hair: [{ key: 'hair', label: t(shaved ? 'person.row_brow' : 'person.row_hair') }],
      gear,
      jersey: [
        { key: 'main', label: t('person.jersey_body') },
        { key: 'accent', label: t(classic ? 'person.jersey_sleeves' : 'person.jersey_band') },
        { key: 'trim', label: t('person.jersey_trim') },
      ],
    }
  }, [s.headwear, s.glasses, s.jersey, s.gender, s.face, shaved, t])

  if (loading) return <View style={styles.loading}><ActivityIndicator color={tc.rosaText} /></View>

  // Blocco colori (chip + palette) di un gruppo di slot; stateKey = dove ricordare lo slot attivo
  const renderColors = (list, stateKey) => {
    if (!list.length) return null
    const act = list.some(x => x.key === slot[stateKey]) ? slot[stateKey] : list[0].key
    const lab = list.find(x => x.key === act)?.label ?? ''
    const pal = PERSON_PALETTES[PERSON_COLOR_PALETTE[act]]
    return (
      <View style={[styles.colorBox, { borderColor: tc.border }]}>
        {list.length > 1
          ? <SlotChips slots={list} active={act} onSelect={(k) => setSlot(prev => ({ ...prev, [stateKey]: k }))} values={s} tc={tc} styles={styles} />
          : <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{lab}</Text>}
        <Palette colors={pal} value={s[act]} onSelect={(c) => update({ [act]: c })} label={lab} tc={tc} styles={styles} />
      </View>
    )
  }

  const slots = tab === 'gear' ? [] : slotsByTab[tab]
  const activeKey = slots.some(x => x.key === slot[tab]) ? slot[tab] : slots[0]?.key
  const activeLabel = slots.find(x => x.key === activeKey)?.label ?? ''
  const palette = activeKey ? PERSON_PALETTES[PERSON_COLOR_PALETTE[activeKey]] : null

  return (
    <View>
      <Text style={[styles.hint, { color: tc.textDim }]}>{t('person.hint')}</Text>

      {specials.length > 0 && (
        <Segment
          options={[{ key: 'mine', k: 'view_mine' }, ...specials.map(x => ({ key: x.code, k: `view_${x.code}` }))]}
          selected={view}
          onSelect={setView}
          tc={tc}
          styles={styles}
        />
      )}
      <View style={styles.preview}>
        <PersonAvatar person={s} size={210} special={special ? special.code : null} />
      </View>
      {special && (
        <Text style={[styles.hint, { color: tc.textMuted, textAlign: 'center', marginBottom: 10 }]}>
          {t(`person.${special.code}_since`, {
            league: leagueName ?? '',
            date: special.awardedAt ? new Date(special.awardedAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) : '',
          })}
        </Text>
      )}

      <View style={styles.tabRow} accessibilityRole="tablist">
        {TABS.map(tb => {
          const on = tab === tb.key
          return (
            <TouchableOpacity
              key={tb.key}
              onPress={() => setTab(tb.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected: on }}
              style={[styles.tabBtn, { backgroundColor: on ? tc.rosaSubtle : 'transparent', borderColor: on ? tc.rosa : 'transparent' }]}
            >
              <MaterialCommunityIcons name={tb.icon} size={20} color={on ? tc.rosaText : tc.textMuted} />
              <Text style={[styles.tabText, { color: on ? tc.text : tc.textMuted }]} numberOfLines={1}>{t(`person.${tb.k}`)}</Text>
            </TouchableOpacity>
          )
        })}
      </View>

      {tab === 'face' && (
        <>
          <Segment
            options={[{ key: 'man', k: 'man' }, { key: 'woman', k: 'woman' }]}
            selected={s.gender}
            onSelect={(gender) => update({ gender, style: 'h1', beard: undefined, beardA: undefined, fringe: undefined, face: undefined, brow: undefined })}
            tc={tc} styles={styles}
          />
          {(
            <>
              <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('person.avatar_face')}</Text>
              <Segment options={PERSON_FACES} selected={s.face ?? ''} onSelect={(face) => update({ face: face || undefined })} tc={tc} styles={styles} />
              <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('person.avatar_brow')}</Text>
              <Segment options={PERSON_BROWS} selected={s.brow ?? 0} onSelect={(brow) => update({ brow: brow || undefined })} tc={tc} styles={styles} />
            </>
          )}
        </>
      )}
      {tab === 'hair' && (
        <>
          <Segment options={PERSON_STYLES[s.gender]} selected={s.style} onSelect={(style) => update({ style })} tc={tc} styles={styles} />
          <Segment
            options={[{ key: false, k: 'hair_flip_off' }, { key: true, k: 'hair_flip_on' }]}
            selected={!!s.flip}
            onSelect={(flip) => update({ flip: flip || undefined })}
            tc={tc} styles={styles}
          />
          {s.gender === 'man' && (
            <>
              <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('person.beard')}</Text>
              <Segment
                options={[{ key: 0, k: 'beard_none' }, ...PERSON_BEARDS]}
                selected={s.beard ?? 0}
                onSelect={(b) => update({ beard: b || undefined, beardA: b ? s.beardA : undefined })}
                tc={tc} styles={styles}
              />
              {!!s.beard && (
                <Segment
                  options={[{ key: 1, k: 'beard_op_normal' }, { key: BEARD_LIGHT, k: 'beard_op_light' }]}
                  selected={s.beardA ?? 1}
                  onSelect={(a) => update({ beardA: a === 1 ? undefined : a })}
                  tc={tc} styles={styles}
                />
              )}
            </>
          )}
          {s.gender === 'woman' && (
            <>
              <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('person.fringe')}</Text>
              <Segment
                options={[{ key: false, k: 'fringe_off' }, { key: true, k: 'fringe_on' }]}
                selected={!!s.fringe}
                onSelect={(f) => update({ fringe: f || undefined })}
                tc={tc} styles={styles}
              />
            </>
          )}
        </>
      )}
      {tab === 'gear' && (
        <>
          <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('person.headwear')}</Text>
          <Segment options={PERSON_HEADWEAR} selected={s.headwear} onSelect={(headwear) => update({ headwear })} tc={tc} styles={styles} />
          {renderColors(slotsByTab.gear, 'gear')}
          <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('person.glasses')}</Text>
          <Segment
            options={[{ key: false, k: 'glasses_off' }, { key: true, k: 'glasses_on' }]}
            selected={s.glasses}
            onSelect={(glasses) => update({ glasses })}
            tc={tc} styles={styles}
          />
          {renderColors(slotsByTab.glass, 'glass')}
        </>
      )}
      {tab === 'jersey' && (
        <Segment options={PERSON_JERSEYS_SELECTABLE} selected={s.jersey} onSelect={(j) => update({ jersey: j })} tc={tc} styles={styles} />
      )}

      {slots.length > 0 && (
        <View style={[styles.colorBox, { borderColor: tc.border }]}>
          {slots.length > 1 && (
            <SlotChips slots={slots} active={activeKey} onSelect={(k) => setSlot(prev => ({ ...prev, [tab]: k }))} values={s} tc={tc} styles={styles} />
          )}
          {slots.length === 1 && <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{activeLabel}</Text>}
          {palette && (
            <Palette colors={palette} value={s[activeKey]} onSelect={(c) => update({ [activeKey]: c })} label={activeLabel} tc={tc} styles={styles} />
          )}
        </View>
      )}

      {msg && <Text style={[styles.msg, { color: msg.ok ? tc.success : tc.rosaText }]}>{msg.text}</Text>}
      <TouchableOpacity
        onPress={handleSave}
        disabled={saving}
        style={[styles.saveBtn, { backgroundColor: saving ? tc.bgCard2 : tc.rosa }]}
        accessibilityRole="button"
      >
        <Text style={[styles.segmentText, { color: saving ? tc.textDim : tc.brandText }]}>{saving ? '...' : t('person.save')}</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={handleReset} disabled={saving} style={styles.resetBtn} accessibilityRole="button">
        <Text style={[styles.resetText, { color: tc.textMuted }]}>{t('person.reset')}</Text>
      </TouchableOpacity>
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  loading: { padding: 24, alignItems: 'center' },
  hint: { fontSize: 12, marginBottom: 6 },
  preview: { alignItems: 'center', marginBottom: 8 },
  tabRow: { flexDirection: 'row', gap: 6, marginBottom: 12 },
  tabBtn: { flex: 1, minHeight: 52, borderWidth: 1, borderRadius: radius.lg, alignItems: 'center', justifyContent: 'center', gap: 2, paddingVertical: 6 },
  tabText: { fontFamily: fonts.condensedBold, fontSize: 12, letterSpacing: 0.3 },
  fieldLabel: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 4 },
  segmentRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  segmentBtn: { flexGrow: 1, flexBasis: '22%', minHeight: 44, borderWidth: 1, borderRadius: radius.lg, paddingHorizontal: 4, alignItems: 'center', justifyContent: 'center' },
  segmentText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  colorBox: { borderTopWidth: 1, paddingTop: 12, marginTop: 2, marginBottom: 6 },
  slotRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  slotChip: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 44, borderWidth: 1, borderRadius: radius.lg, paddingHorizontal: 12 },
  slotDot: { width: 20, height: 20, borderRadius: 10, borderWidth: 1 },
  slotText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  swatchRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 6 },
  swatch: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  msg: { fontSize: 13, marginTop: 6, marginBottom: 8 },
  saveBtn: { borderRadius: radius.lg, minHeight: 48, alignItems: 'center', justifyContent: 'center', marginTop: 6 },
  resetBtn: { minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  resetText: { fontFamily: fonts.condensedBold, fontSize: 13 },
})
